/**
 * Resolves the display name shown in the site header.
 *
 * Priority per spec:
 *   1. name + family_name (trimmed, joined with space) – if either exists
 *   2. email local part (substring before '@', trimmed)
 *   3. phone number (trimmed, as-stored e.g. 09XXXXXXXXX)
 *   4. fallback "کاربر"
 *
 * Accepts the shape returned by /api/auth/me (UserResponse) and tolerates
 * null/undefined/empty strings and both `family_name` and `familyName` keys.
 */

export type DisplayUser = {
  name?: string | null;
  family_name?: string | null;
  familyName?: string | null;
  email?: string | null;
  phone?: string | null;
  username?: string | null; // accepted but intentionally not used per spec
} | null | undefined;

function trimmed(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

export function getDisplayName(user: DisplayUser): string {
  if (!user) return "کاربر";

  const name = trimmed(user.name);
  const family = trimmed((user as Record<string, unknown>).family_name ?? (user as Record<string, unknown>).familyName);

  const fullName = [name, family].filter(Boolean).join(" ").trim();
  if (fullName) return fullName;

  const email = trimmed(user.email);
  if (email) {
    const at = email.indexOf("@");
    return at > 0 ? email.slice(0, at) : email;
  }

  const phone = trimmed(user.phone);
  if (phone) return phone;

  return "کاربر";
}

export function getInitial(user: DisplayUser, fallback = "A"): string {
  const dn = getDisplayName(user);
  if (dn === "کاربر") return fallback;
  const ch = dn.trim().charAt(0);
  return ch ? ch.toUpperCase() : fallback;
}
